/**
 * The two predicates every hand-rolled keyboard shortcut in this app needs.
 *
 * There is no hotkey library here — shortcuts are `document`-level `keydown` listeners — so
 * "which modifier does this platform mean by *primary*" and "is the user typing right now"
 * are questions each one has to answer for itself. They are answered here once, because the
 * previous arrangement had `isApplePlatform` copy-pasted verbatim into two lib files and the
 * typing guard hidden inside a tree-navigation hook.
 */

/**
 * True on macOS/iOS, where the primary shortcut modifier is Cmd rather than Ctrl.
 *
 * `navigator.userAgentData.platform` is the non-deprecated answer and the only one that stays
 * honest on Chromium; `navigator.platform` is the fallback for browsers that do not ship it.
 */
export function isApplePlatform() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgentDataPlatform =
    "userAgentData" in navigator
      ? (
          navigator as Navigator & {
            userAgentData?: { platform?: string };
          }
        ).userAgentData?.platform
      : undefined;

  if (userAgentDataPlatform) {
    return /macOS|iOS/i.test(userAgentDataPlatform);
  }

  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
}

/**
 * True when the event target is somewhere the keystroke is text, not a command.
 *
 * Deliberately a *typing* guard and not an interactive-element guard: a shortcut must stay
 * out of inputs and editors, but a button or a menu item is exactly where a bare key like
 * Delete is still meant to work. (`features/framework-admin/components/structure/tree.tsx`
 * keeps its own, wider guard for the other case.)
 */
export function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true'], [role='textbox']")
  );
}
